import {
  convertModelDefinition,
  listDef,
  valueDef,
} from "@recare/react-forms-state";
import { validateFormCreationDate } from "./FormSections/SectionHeader";

export const REQUEST_FORM_FIELDS = {
  CAREPROVIDER_ADDRESS_ADDRESS: "careprovider_address_address",
  CAREPROVIDER_ADDRESS_CITY: "careprovider_address_city",
  CAREPROVIDER_ADDRESS_ZIPCODE: "careprovider_address_zipcode",
  CAREPROVIDER_NAME: "careprovider_name",
  REQUEST_SENT_DATE: "request_sent_date",
  REQUEST_SENT_TIME: "request_sent_time",
  REQUEST_STATUS: "request_status",
} as const;

export type TransitionalCareRequest = {
  [REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ADDRESS]: string;
  [REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_CITY]: string;
  [REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ZIPCODE]: string;
  [REQUEST_FORM_FIELDS.CAREPROVIDER_NAME]: string;
  [REQUEST_FORM_FIELDS.REQUEST_SENT_DATE]: number | null;
  [REQUEST_FORM_FIELDS.REQUEST_SENT_TIME]: number | null;
  [REQUEST_FORM_FIELDS.REQUEST_STATUS]: number | null;
  edited: boolean;
  id: number;
  manual: boolean;
  request_id: number | null;
};

export type TransitionalCareFormData = {
  aftercare_application_date?: number;
  aftercare_application_explanation?: string;
  aftercare_application_submitted?: boolean;
  aftercare_care_level_applied?: boolean;
  aftercare_care_level_applied_date?: number;
  aftercare_care_level_available?: boolean;
  aftercare_care_level_explanation?: string;
  aftercare_medical_rehab?: boolean;
  aftercare_mobile?: boolean;
  aftercare_shortterm_care?: boolean;
  aftercare_start_date?: number;
  care_necessity?: number;
  clinic_ik_number?: string;
  clinic_location_city?: string;
  clinic_location_street?: string;
  clinic_location_street_number?: string;
  clinic_location_zipcode?: string;
  clinic_name?: string;
  insurance_form_sent_date?: number;
  internet_portal_used?: boolean;
  legal_representative_first_name?: string;
  legal_representative_last_name?: string;
  legal_representative_location_city?: string;
  legal_representative_location_street?: string;
  legal_representative_location_street_number?: string;
  legal_representative_location_zipcode?: string;
  patient_date_of_birth?: number;
  patient_first_name?: string;
  patient_insurance_number?: string;
  patient_last_name?: string;
  patient_location_city?: string;
  patient_location_house_number?: string;
  patient_location_street?: string;
  patient_location_zipcode?: string;
  prior_treatment_end_date?: number;
  prior_treatment_hospital_id?: string;
  prior_treatment_start_date?: number;
  request_list?: TransitionalCareRequest[];
  social_worker_email?: string;
  social_worker_fax?: string;
  social_worker_first_name?: string;
  social_worker_last_name?: string;
  social_worker_telephone?: string;
  transitional_care_department_code?: string;
  transitional_care_description?: string;
  transitional_care_end_date?: number;
  transitional_care_location_id?: string;
  transitional_care_patient_confirmation?: boolean;
  transitional_care_patient_confirmation_date?: number;
  transitional_care_patient_confirmation_explanation?: string;
  transitional_care_start_date?: number;
  transitional_care_start_search_date?: number;
  transitional_care_suitable_providers_count?: number;
  transitional_care_suitable_providers_more_than_twenty?: boolean;
};

export const transitionalCareModelDefinition = convertModelDefinition({
  // form creation date
  ...valueDef("created_at", {
    validate: (value, props) =>
      validateFormCreationDate(value, props.translations),
    fieldRequired: true,
    errorString: (t) => t.general.required,
  }),

  // clinic section
  ...valueDef("clinic_name"),
  ...valueDef("clinic_ik_number"),
  ...valueDef("clinic_location_street"),
  ...valueDef("clinic_location_street_number"),
  ...valueDef("clinic_location_zipcode"),
  ...valueDef("clinic_location_city"),

  // social worker section
  ...valueDef("social_worker_last_name"),
  ...valueDef("social_worker_first_name"),
  ...valueDef("social_worker_telephone"),
  ...valueDef("social_worker_fax"),
  ...valueDef("social_worker_email"),

  // patient contact section
  ...valueDef("patient_last_name"),
  ...valueDef("patient_first_name"),
  ...valueDef("patient_date_of_birth"),
  ...valueDef("patient_insurance_number"),
  ...valueDef("patient_location_street"),
  ...valueDef("patient_location_house_number"),
  ...valueDef("patient_location_zipcode"),
  ...valueDef("patient_location_city"),

  // legal representation section
  ...valueDef("confirmed_with_legal_rep"),
  ...valueDef("legal_representative_last_name"),
  ...valueDef("legal_representative_first_name"),
  ...valueDef("legal_representative_location_street"),
  ...valueDef("legal_representative_location_street_number"),
  ...valueDef("legal_representative_location_zipcode"),
  ...valueDef("legal_representative_location_city"),

  // prior treatment section
  ...valueDef("prior_treatment_start_date"),
  ...valueDef("prior_treatment_end_date"),
  ...valueDef("prior_treatment_hospital_id"),

  // transitional care section
  ...valueDef("transitional_care_start_date"),
  ...valueDef("transitional_care_end_date"),
  ...valueDef("transitional_care_location_id"),
  ...valueDef("transitional_care_department_code"),

  // section a
  ...valueDef("transitional_care_start_search_date"),

  // section b
  ...valueDef("transitional_care_patient_confirmation"),
  ...valueDef("transitional_care_patient_confirmation_date"),
  ...valueDef("transitional_care_patient_confirmation_explanation"),
  ...valueDef("transitional_care_description"),
  ...valueDef("transitional_care_suitable_providers_more_than_twenty"),
  ...valueDef("transitional_care_suitable_providers_count", {
    convertIn: (value) => value ?? 0,
  }),

  // section c
  ...valueDef("aftercare_mobile"),
  ...valueDef("aftercare_shortterm_care"),
  ...valueDef("aftercare_medical_rehab"),
  ...valueDef("aftercare_application_submitted"),
  ...valueDef("aftercare_application_date"),
  ...valueDef("aftercare_application_explanation"),
  ...valueDef("aftercare_care_level_available"),
  ...valueDef("aftercare_care_level_applied"),
  ...valueDef("aftercare_care_level_applied_date"),
  ...valueDef("aftercare_care_level_explanation"),

  // section d
  ...valueDef("insurance_form_sent_date"),

  // section e
  ...valueDef("internet_portal_used", { defaultValue: true }),
  ...listDef("request_list"),

  // section f
  ...valueDef("care_necessity"),
  ...valueDef("aftercare_start_date"),
});
